import { Bell, BellOff, Check, Copy, Link, Settings, Trash2 } from 'lucide-react'
import { ContextMenu, MenuCopy, MenuGroup, MenuItem, MenuRadio, MenuSeparator } from '../ContextMenu'
import { channelLink } from '@/lib/appLinks'
import { isUnread, threadsOf } from '@/lib/guildUi'
import { NOTIFY_ALL, NOTIFY_INHERIT, NOTIFY_MENTIONS, NOTIFY_NOTHING, channelLevel, guildLevel, guildMuted, mutedAncestor, ownMuted } from '@/lib/muting'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { useSocialStore } from '@/store/social'
import { ChannelType, type Channel, type Guild } from '@/types/api'

const LEVEL_NAMES: Record<number, string> = {
  [NOTIFY_ALL]: 'Все сообщения',
  [NOTIFY_MENTIONS]: 'Только @упоминания',
  [NOTIFY_NOTHING]: 'Ничего',
}

const LEVELS = [NOTIFY_INHERIT, NOTIFY_ALL, NOTIFY_MENTIONS, NOTIFY_NOTHING] as const

// что выбрано у сервера — коротко, справа от «Как на сервере»
const LEVEL_HINTS: Record<number, string> = {
  [NOTIFY_ALL]: 'все',
  [NOTIFY_MENTIONS]: 'упоминания',
  [NOTIFY_NOTHING]: 'ничего',
}

/** Отметить прочитанными каналы и их активные ветки: то, что в списке выглядит непрочитанным. */
function useMarkRead(guildId: string, list: Channel[]) {
  const channels = useGuildStore((s) => s.channels[guildId])
  const readStates = useSocialStore((s) => s.readStates)
  const ack = useSocialStore((s) => s.ack)
  const targets = list.filter((c) => c.type === ChannelType.GuildText || c.type === ChannelType.GuildVoice || c.type === ChannelType.GuildForum).flatMap((c) => c.type === ChannelType.GuildText || c.type === ChannelType.GuildForum ? [c, ...threadsOf(channels, c.id)] : [c])
  const unread = targets.filter((c) => isUnread(c, readStates[c.id]) || (readStates[c.id]?.mention_count ?? 0) > 0)
  return {
    unread: unread.length > 0,
    markRead: () => {
      for (const c of unread) if (c.last_message_id) void ack(c.id, c.last_message_id)
    },
  }
}

/**
 * Меню канала по правой кнопке: прочитано, mute, уровень уведомлений, ссылка, настройки.
 * Уровень «Как на сервере» показывает, что именно выбрано у сервера.
 */
export function ChannelContextMenu({ guild, channel, x, y, canManage, onSettings, onClose }: { guild: Guild; channel: Channel; x: number; y: number; canManage: boolean; onSettings: () => void; onClose: () => void }) {
  const channels = useGuildStore((s) => s.channels[guild.id])
  const settings = useGuildStore((s) => s.settings[guild.id])
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const devMode = useAppStore((s) => s.devMode)
  const text = channel.type === ChannelType.GuildText || channel.type === ChannelType.GuildVoice || channel.type === ChannelType.GuildForum
  const { unread, markRead } = useMarkRead(guild.id, [channel])
  const own = ownMuted(settings, channel.id)
  // заглушено не самим каналом: подсказка у пункта объясняет, почему канал серый
  const inherited = own ? null : guildMuted(settings) ? 'сервер заглушен' : mutedAncestor(settings, channel.id, channels) ? 'категория заглушена' : null
  const level = channelLevel(settings, channel.id)
  const serverLevel = guildLevel(settings, guild.default_message_notifications)
  const patch = (o: { muted?: boolean; message_notifications?: number }) => {
    void updateSettings(guild.id, { channel_overrides: { [channel.id]: o } }).catch(() => undefined)
    onClose()
  }

  return (
    <ContextMenu x={x} y={y} label={`Канал ${channel.name ?? ''}`} onClose={onClose}>
      {text ? (
        <MenuItem icon={<Check size={16} />} label="Отметить как прочитанное" disabled={!unread} onClick={() => { markRead(); onClose() }} />
      ) : null}
      <MenuItem icon={own ? <Bell size={16} /> : <BellOff size={16} />} label={own ? 'Включить уведомления' : 'Заглушить канал'} hint={inherited ?? undefined} onClick={() => patch({ muted: !own })} />
      {text ? (
        <>
          <MenuSeparator />
          <MenuGroup>Уведомления</MenuGroup>
          {LEVELS.map((value) => (
            <MenuRadio
              key={value}
              label={value === NOTIFY_INHERIT ? 'Как на сервере' : (LEVEL_NAMES[value] ?? '')}
              hint={value === NOTIFY_INHERIT ? LEVEL_HINTS[serverLevel] : undefined}
              checked={level === value}
              onClick={() => patch({ message_notifications: value })}
            />
          ))}
        </>
      ) : null}
      <MenuSeparator />
      <MenuCopy icon={<Link size={16} />} label="Скопировать ссылку на канал" done="Ссылка скопирована" text={channelLink(location.origin, guild.id, channel.id)} onDone={onClose} />
      {devMode ? <MenuCopy icon={<Copy size={16} />} label="Скопировать ID" done="ID скопирован" text={channel.id} onDone={onClose} /> : null}
      {canManage ? <MenuItem icon={<Settings size={16} />} label="Настройки канала" onClick={onSettings} /> : null}
    </ContextMenu>
  )
}

/** Меню категории: прочитать всё в ней и заглушить её целиком — каналы внутри замолкают вместе с ней. */
export function CategoryContextMenu({ guild, category, items, x, y, canManage, onSettings, onDelete, onClose }: { guild: Guild; category: Channel; items: Channel[]; x: number; y: number; canManage: boolean; onSettings: () => void; onDelete: () => void; onClose: () => void }) {
  const settings = useGuildStore((s) => s.settings[guild.id])
  const updateSettings = useGuildStore((s) => s.updateSettings)
  const devMode = useAppStore((s) => s.devMode)
  const { unread, markRead } = useMarkRead(guild.id, items)
  const muted = ownMuted(settings, category.id)

  return (
    <ContextMenu x={x} y={y} label={`Категория ${category.name ?? ''}`} onClose={onClose}>
      <MenuItem icon={<Check size={16} />} label="Отметить как прочитанное" disabled={!unread} onClick={() => { markRead(); onClose() }} />
      <MenuItem
        icon={muted ? <Bell size={16} /> : <BellOff size={16} />}
        label={muted ? 'Включить уведомления' : 'Заглушить категорию'}
        onClick={() => {
          void updateSettings(guild.id, { channel_overrides: { [category.id]: { muted: !muted } } }).catch(() => undefined)
          onClose()
        }}
      />
      {canManage ? (
        <>
          <MenuSeparator />
          <MenuItem icon={<Settings size={16} />} label="Настройки категории" onClick={onSettings} />
          <MenuItem icon={<Trash2 size={16} />} label="Удалить категорию" danger onClick={onDelete} />
        </>
      ) : null}
      {devMode ? (
        <>
          <MenuSeparator />
          <MenuCopy icon={<Copy size={16} />} label="Скопировать ID" done="ID скопирован" text={category.id} onDone={onClose} />
        </>
      ) : null}
    </ContextMenu>
  )
}
